import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { getDb, functions, getCallableName } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { NewsSource } from '../types';

const COLLECTION_NAME = 'news_sources';

export interface ListSourcesParams {
  pageSize?: number;
  lastDoc?: QueryDocumentSnapshot<DocumentData> | null;
  searchQuery?: string;
}

export interface ListSourcesResult {
  sources: NewsSource[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

/**
 * List news sources with database-level filtering and pagination
 */
export async function listSources({
  pageSize = 50,
  lastDoc = null,
  searchQuery,
}: ListSourcesParams = {}): Promise<ListSourcesResult> {
  try {
    let q = query(collection(getDb(), COLLECTION_NAME), orderBy('name', 'asc'));

    // Add pagination
    q = query(q, limit(pageSize + 1)); // Fetch one extra to check if there are more

    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const querySnapshot = await getDocs(q);
    const docs = querySnapshot.docs;

    // Check if there are more results
    const hasMore = docs.length > pageSize;
    const sourceDocs = hasMore ? docs.slice(0, pageSize) : docs;

    let sources = sourceDocs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as NewsSource[];

    // Client-side search filter (only if we have a small result set)
    // For better performance, implement searchKeywords array in Firestore
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      sources = sources.filter(
        source =>
          source.name.toLowerCase().includes(search) || source.url.toLowerCase().includes(search)
      );
    }

    return {
      sources,
      lastDoc: sourceDocs.length > 0 ? sourceDocs[sourceDocs.length - 1] : null,
      hasMore,
    };
  } catch (error) {
    console.error('Error listing news sources:', error);
    throw error;
  }
}

/**
 * Create a new news source
 */
export async function createSource(
  sourceData: Omit<NewsSource, 'id' | 'lastFetchedAt'>
): Promise<string> {
  try {
    const docRef = await addDoc(collection(getDb(), COLLECTION_NAME), {
      ...sourceData,
      lastFetchedAt: Timestamp.fromDate(new Date(0)), // Unix epoch
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating news source:', error);
    throw error;
  }
}

/**
 * Update an existing news source
 */
export async function updateSource(
  sourceId: string,
  sourceData: Partial<Omit<NewsSource, 'id'>>
): Promise<void> {
  try {
    // Remove undefined values to avoid Firestore errors
    const cleanedData = Object.fromEntries(
      Object.entries(sourceData).filter(([_, value]) => value !== undefined)
    );
    await updateDoc(doc(getDb(), COLLECTION_NAME, sourceId), cleanedData);
  } catch (error) {
    console.error('Error updating news source:', error);
    throw error;
  }
}

/**
 * Delete a news source
 */
export async function deleteSource(sourceId: string): Promise<void> {
  try {
    await deleteDoc(doc(getDb(), COLLECTION_NAME, sourceId));
  } catch (error) {
    console.error('Error deleting news source:', error);
    throw error;
  }
}

/**
 * Toggle the active status of a news source
 */
export async function toggleSourceActive(sourceId: string, isActive: boolean): Promise<void> {
  try {
    await updateDoc(doc(getDb(), COLLECTION_NAME, sourceId), { isActive });
  } catch (error) {
    console.error('Error toggling news source active status:', error);
    throw error;
  }
}

/**
 * Trigger news processing Cloud Function
 */
export async function triggerNewsProcessing(): Promise<{ success: boolean; message: string }> {
  try {
    const triggerProcessing = httpsCallable(functions, getCallableName('triggerNewsProcessing'));
    const result = await triggerProcessing();
    return result.data as { success: boolean; message: string };
  } catch (error) {
    console.error('Error triggering news processing:', error);
    throw error;
  }
}
